import { useCallback, useEffect, useState } from "react";
import {
  BellRinging,
  BellSlash,
  Desktop,
  DeviceMobile,
  Trash,
  ShieldCheck,
  EnvelopeSimple,
  ChatCircleDots,
  WhatsappLogo,
  Info,
  Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  disableCurrentDevice,
  getPushStatus,
  isPushSupported,
  parseUserAgent,
  formatRelative,
  subscribeDevice,
  unsubscribeDevice,
} from "../../lib/pushT21";

/**
 * Sprint T-21 · Fase 1b · Panel de Preferencias de Notificaciones.
 *
 * Componente reutilizable que gestiona:
 *   · Toggle Push en el dispositivo actual (registro SW + subscribe/unsubscribe)
 *   · Listado multi-dispositivo con botón desuscribir individual
 *   · Estado informativo de los otros canales (Email · SMS futuro · WhatsApp futuro)
 *
 * Props:
 *   · apiClient       — instancia axios autenticada (api | customerApi)
 *   · variant         — "customer" (Portal Cliente · fondos claros)
 *                       | "shop"   (Portal Taller · fondos oscuros)
 *   · className       — clases extra opcionales
 *
 * NO conoce la diferencia entre user y customer · consume la API T-21 unificada.
 */
export function NotificationPreferencesCard({ apiClient, variant = "customer", className = "" }) {
  const [status, setStatus] = useState({
    supported: false,
    permission: "default",
    currentSubscribed: false,
    devices: [],
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await getPushStatus(apiClient);
      setStatus(s);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => { refresh(); }, [refresh]);

  const isShop = variant === "shop";

  // Enable current device
  const onEnable = async () => {
    setBusy(true);
    try {
      await subscribeDevice(apiClient);
      toast.success("Notificaciones activadas en este dispositivo");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "No se pudieron activar las notificaciones");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  // Disable current device
  const onDisableCurrent = async () => {
    setBusy(true);
    try {
      await disableCurrentDevice(apiClient);
      toast.success("Notificaciones desactivadas en este dispositivo");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "No se pudieron desactivar las notificaciones");
    } finally {
      setBusy(false);
    }
  };

  // Remove a specific device by id
  const onRemoveDevice = async (id) => {
    setBusy(true);
    try {
      await unsubscribeDevice(apiClient, id);
      toast.success("Dispositivo desuscrito");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "No se pudo desuscribir");
    } finally {
      setBusy(false);
    }
  };

  // Styling helpers based on variant
  // Sprint T-22 · WCAG AA fix (variant='shop')
  // - `text-[var(--ag-text)]` heredado quedaba con contraste bajo en algunos temas · forzamos `text-white`.
  // - `dimText` en shop pasa de gray-400 (#9CA3AF · 4.4:1 sobre #0F172A) a gray-300 (#D1D5DB · 9.7:1 · AAA).
  const container = isShop
    ? "bg-[#0F172A] border border-[#1F2937] text-white"
    : "bg-white border border-gray-200 text-[#1A1A1A]";
  const softText = isShop ? "text-gray-200" : "text-gray-600";
  const dimText = isShop ? "text-gray-300" : "text-gray-500";
  const rowBg = isShop
    ? "bg-[#101827] border-[#1F2937]"
    : "bg-gray-50 border-gray-200";
  const brand = isShop ? "text-[#FFB703]" : "text-[#0033A0]";

  if (loading) {
    return (
      <div className={`${container} rounded-md p-6 ${className}`} data-testid="notif-prefs-loading">
        <div className={`text-sm ${dimText}`}>Cargando preferencias…</div>
      </div>
    );
  }

  return (
    <div
      className={`${container} rounded-md overflow-hidden ${className}`}
      data-testid="notification-preferences-card"
    >
      {/* Header */}
      <div className={`px-5 py-4 border-b ${isShop ? "border-[#1F2937]" : "border-gray-200"}`}>
        <div className="flex items-center gap-2">
          <BellRinging size={20} weight="fill" className={brand} />
          <h3 className="font-display font-bold text-lg tracking-tight">Preferencias de notificaciones</h3>
        </div>
        <p className={`text-xs mt-1 ${dimText}`}>
          Gestiona cómo te avisamos: recordatorios de cita, cambios de estado y mensajes de tu taller.
        </p>
      </div>

      {/* SECTION · Push */}
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className={`shrink-0 w-9 h-9 rounded-md grid place-items-center ${
              isShop ? "bg-[#FFB703]/15" : "bg-[#0033A0]/10"
            }`}>
              <BellRinging size={18} weight="fill" className={brand} />
            </div>
            <div>
              <div className="font-semibold text-sm">Notificaciones push</div>
              <div className={`text-xs mt-0.5 ${softText}`}>
                Recibe alertas instantáneas en este dispositivo, incluso con el navegador cerrado.
              </div>
            </div>
          </div>
          <PushStatusPill status={status} isShop={isShop} />
        </div>

        {!status.supported && (
          <div className={`text-xs px-3 py-2 rounded-sm ${
            isShop ? "bg-[#1F2937]/60 text-gray-300" : "bg-amber-50 text-amber-900 border border-amber-200"
          }`} data-testid="push-unsupported-notice">
            Tu navegador no soporta notificaciones push. Prueba con Chrome, Firefox o Safari 16+ en un
            dispositivo compatible.
          </div>
        )}

        {status.supported && status.permission === "denied" && (
          <div className={`text-xs px-3 py-2 rounded-sm flex items-start gap-2 ${
            isShop ? "bg-red-500/10 text-red-300 border border-red-500/30" : "bg-red-50 text-red-800 border border-red-200"
          }`} data-testid="push-denied-notice">
            <BellSlash size={14} weight="fill" className="mt-0.5 shrink-0" />
            <span>
              Has bloqueado las notificaciones para este sitio. Habilítalas desde los ajustes de tu navegador
              (icono del candado en la barra de dirección → Notificaciones → Permitir) y vuelve a esta página.
            </span>
          </div>
        )}

        {status.supported && status.permission !== "denied" && (
          <div className="flex flex-wrap items-center gap-2" data-testid="push-primary-actions">
            {status.currentSubscribed ? (
              <Button
                type="button"
                size="sm"
                disabled={busy}
                onClick={onDisableCurrent}
                variant="outline"
                className={`rounded-sm ${
                  isShop ? "border-white/20 text-white hover:bg-white/5" : "border-gray-300"
                }`}
                data-testid="push-disable-current-device"
              >
                <BellSlash size={14} weight="bold" className="mr-1.5" />
                Desactivar en este dispositivo
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                disabled={busy}
                onClick={onEnable}
                className={
                  isShop
                    ? "bg-[#FFB703] text-black hover:opacity-90 rounded-sm font-semibold"
                    : "bg-[#0033A0] text-white hover:opacity-90 rounded-sm font-semibold"
                }
                data-testid="push-enable-current-device"
              >
                <BellRinging size={14} weight="fill" className="mr-1.5" />
                {busy ? "Activando…" : "Activar en este dispositivo"}
              </Button>
            )}
            <span className={`text-[11px] ${dimText}`}>Se pedirá el permiso del navegador la primera vez.</span>
          </div>
        )}

        {/* Devices list */}
        {status.devices.length > 0 && (
          <div className="mt-2" data-testid="push-devices-list">
            <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${dimText} mb-2`}>
              Dispositivos suscritos · {status.devices.length}
            </div>
            <div className="space-y-2">
              {status.devices.map((dev) => (
                <DeviceRow
                  key={dev.id}
                  dev={dev}
                  isShop={isShop}
                  rowBg={rowBg}
                  softText={softText}
                  dimText={dimText}
                  onRemove={() => onRemoveDevice(dev.id)}
                  busy={busy}
                />
              ))}
            </div>
          </div>
        )}

        {status.supported && status.devices.length === 0 && (
          <div className={`text-xs px-3 py-3 rounded-sm border-dashed border ${
            isShop ? "bg-transparent border-[#1F2937] text-gray-400" : "bg-gray-50 border-gray-200 text-gray-500"
          }`} data-testid="push-devices-empty">
            Aún no has activado las notificaciones en ningún dispositivo.
          </div>
        )}
      </div>

      {/* SECTION · Otros canales (informativo) */}
      <div className={`px-5 py-4 border-t ${isShop ? "border-[#1F2937] bg-[#0B1220]" : "border-gray-200 bg-gray-50"}`}>
        <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${dimText} mb-3`}>
          Otros canales
        </div>
        <div className="space-y-2.5">
          <ChannelRow
            icon={EnvelopeSimple}
            label="Email"
            status={
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${
                isShop ? "text-emerald-400" : "text-emerald-700"
              }`}>
                <Check size={11} weight="bold" /> Siempre activo
              </span>
            }
            hint="Recibirás confirmaciones y recordatorios de cita en el email de tu cuenta."
            isShop={isShop}
            softText={softText}
            data-testid="channel-email-row"
          />
          <ChannelRow
            icon={ChatCircleDots}
            label="SMS"
            status={
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${dimText}`}>
                <Info size={11} weight="fill" /> Próximamente
              </span>
            }
            hint="Estamos preparando la integración de SMS transaccional."
            isShop={isShop}
            softText={softText}
            data-testid="channel-sms-row"
          />
          <ChannelRow
            icon={WhatsappLogo}
            label="WhatsApp"
            status={
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${dimText}`}>
                <Info size={11} weight="fill" /> Próximamente
              </span>
            }
            hint="Recordatorios y confirmaciones vía WhatsApp Business, en fases futuras."
            isShop={isShop}
            softText={softText}
            data-testid="channel-whatsapp-row"
          />
        </div>
        <div className={`mt-4 flex items-center gap-1.5 text-[10px] ${dimText}`}>
          <ShieldCheck size={12} weight="fill" />
          <span>Tus preferencias se sincronizan de forma segura con AutoGlow Ecosystem.</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Subcomponentes internos
// ============================================================

function PushStatusPill({ status, isShop }) {
  if (!status.supported) {
    return (
      <span
        className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${
          isShop ? "bg-[#1F2937] text-gray-400" : "bg-gray-200 text-gray-600"
        }`}
        data-testid="push-status-pill"
      >
        No soportado
      </span>
    );
  }
  if (status.permission === "denied") {
    return (
      <span
        className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${
          isShop ? "bg-red-500/20 text-red-300" : "bg-red-100 text-red-700"
        }`}
        data-testid="push-status-pill"
      >
        Bloqueado
      </span>
    );
  }
  if (status.currentSubscribed) {
    return (
      <span
        className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${
          isShop ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"
        }`}
        data-testid="push-status-pill"
      >
        Activo
      </span>
    );
  }
  return (
    <span
      className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${
        isShop ? "bg-[#1F2937] text-gray-300" : "bg-gray-200 text-gray-700"
      }`}
      data-testid="push-status-pill"
    >
      Desactivado
    </span>
  );
}

function DeviceRow({ dev, isShop, rowBg, softText, dimText, onRemove, busy }) {
  const info = parseUserAgent(dev.user_agent);
  const isMobile = info.os === "iOS" || info.os === "Android";
  const Icon = isMobile ? DeviceMobile : Desktop;
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-sm border ${rowBg}`}
      data-testid={`push-device-${dev.id}`}
    >
      <div className={`shrink-0 w-9 h-9 rounded-md grid place-items-center ${
        isShop ? "bg-white/5" : "bg-white border border-gray-200"
      }`}>
        <Icon size={18} weight="duotone" className={isShop ? "text-gray-300" : "text-gray-600"} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" data-testid={`device-label-${dev.id}`}>
          {dev.device_label || info.label}
        </div>
        <div className={`text-[11px] mt-0.5 ${softText}`}>
          Registrado {formatRelative(dev.created_at)}
          {dev.last_used_at ? ` · Último uso ${formatRelative(dev.last_used_at)}` : " · Aún sin uso"}
        </div>
        <div className={`text-[10px] font-mono ${dimText}`}>id · {dev.endpoint_hash}</div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onRemove}
        className={`p-1.5 rounded ${
          isShop ? "text-gray-400 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-600 hover:bg-red-50"
        }`}
        title="Desuscribir este dispositivo"
        aria-label="Desuscribir este dispositivo"
        data-testid={`push-device-remove-${dev.id}`}
      >
        <Trash size={14} weight="bold" />
      </button>
    </div>
  );
}

function ChannelRow({ icon: Icon, label, status, hint, isShop, softText, "data-testid": testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <div className={`shrink-0 w-8 h-8 rounded-md grid place-items-center ${
        isShop ? "bg-white/5 text-gray-300" : "bg-white border border-gray-200 text-gray-600"
      }`}>
        <Icon size={15} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold">{label}</span>
          {status}
        </div>
        <div className={`text-[11px] mt-0.5 ${softText}`}>{hint}</div>
      </div>
    </div>
  );
}
